import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, RefreshCw } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API } from '@/utils/api';
import axios from 'axios';

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const AdminLocations = ({ auth, onLogout }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data.filter((e) => e.is_active));
    } catch (err) { console.error(err); }
  }, []);

  const extractLocations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      const res = await axios.get(`${API}/time/all`, { params: { ...params, limit: 200 } });
      const entries = res.data;

      const empMap = {};
      employees.forEach((e) => { empMap[e.id] = `${e.first_name} ${e.last_name}`; });

      const locs = [];
      entries.forEach((entry) => {
        if (entry.clock_in_location?.latitude && entry.clock_in_location?.longitude) {
          locs.push({
            id: `${entry.id}-in`,
            name: empMap[entry.employee_id] || 'Inconnu',
            type: 'Entrée',
            date: entry.date,
            time: entry.clock_in,
            lat: entry.clock_in_location.latitude,
            lng: entry.clock_in_location.longitude,
          });
        }
        if (entry.clock_out_location?.latitude && entry.clock_out_location?.longitude) {
          locs.push({
            id: `${entry.id}-out`,
            name: empMap[entry.employee_id] || 'Inconnu',
            type: 'Sortie',
            date: entry.date,
            time: entry.clock_out,
            lat: entry.clock_out_location.latitude,
            lng: entry.clock_out_location.longitude,
          });
        }
      });
      setLocations(locs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedEmployee, employees]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { if (employees.length > 0) extractLocations(); }, [employees, extractLocations]);

  const center = locations.length > 0
    ? [locations[0].lat, locations[0].lng]
    : [48.8566, 2.3522]; // Paris default

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Localisations</h1>
            <p className="text-base text-slate-600 mt-1">Carte des pointages GPS</p>
          </div>
          <Button variant="outline" size="sm" onClick={extractLocations} data-testid="refresh-locations">
            <RefreshCw className="mr-2 w-4 h-4" /> Actualiser
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger className="w-64" data-testid="employee-filter">
              <SelectValue placeholder="Tous les employés" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les employés</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">{locations.length} pointage(s) avec GPS</span>
        </div>

        <Card className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="map-container">
          {loading ? (
            <div className="h-96 flex items-center justify-center bg-slate-50">
              <p className="text-slate-500">Chargement de la carte...</p>
            </div>
          ) : locations.length === 0 ? (
            <div className="h-96 flex flex-col items-center justify-center bg-slate-50">
              <MapPin className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun pointage avec localisation GPS</p>
              <p className="text-sm text-slate-400 mt-1">Les localisations apparaîtront quand les employés activeront le GPS</p>
            </div>
          ) : (
            <MapContainer center={center} zoom={13} style={{ height: '500px', width: '100%' }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {locations.map((loc) => (
                <Marker key={loc.id} position={[loc.lat, loc.lng]} icon={loc.type === 'Entrée' ? greenIcon : redIcon}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold">{loc.name}</p>
                      <p>{loc.type} - {loc.date}</p>
                      <p>{fmtTime(loc.time)}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminLocations;
